import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "../../components/ui/Icon";
import { ApiError } from "../../lib/api";
import { ccApi, type Home } from "../../lib/ccApi";
import { IN_APP, onFromApp } from "../../lib/inApp";

export const errText = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

let cached: Home | null = null;
const listeners = new Set<(h: Home) => void>();

/** Read Home and hand it to every screen showing it. */
async function readHome(): Promise<void> {
  const h = await ccApi.home();
  cached = h;
  for (const l of listeners) l(h);
}

/** Load Home ahead of a tap on its tab, unless it is already here. */
export function warmHome(): void {
  if (!cached) readHome().catch(() => {});
}

// Why: inside the phone app a screen shown again reads what it shows again (APP-MODE.md section 4); one read
// reaches every part of the screen showing Home, and a screen not showing it reads nothing.
if (IN_APP)
  onFromApp((m) => {
    if (m.kind === "shown" && listeners.size > 0) readHome().catch(() => {});
  });

/**
 * The Command Center's home data (`GET /home`): products, goals, threads and needs. One copy for the rail and
 * the pages, refreshed on demand and every half minute, so a thread or a decision shows everywhere at once.
 */
export function useHome() {
  const [home, setHome] = useState<Home | null>(cached);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    listeners.add(setHome);
    return () => {
      listeners.delete(setHome);
    };
  }, []);
  const refresh = useCallback(async () => {
    try {
      await readHome();
      setError(null);
    } catch (e) {
      setError(errText(e));
    }
  }, []);
  useEffect(() => {
    void refresh();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, 30_000);
    return () => clearInterval(t);
  }, [refresh]);
  return { home, error, refresh };
}

interface ToastState {
  message: ReactNode;
  on: boolean;
  done: boolean;
  action: { label: string; onClick: () => void } | null;
}

/** The prototype's toast, with an optional Undo button (its `.t-undo`), which keeps the toast up longer. */
export function useCcToast() {
  const [t, setT] = useState<ToastState>({ message: "", on: false, done: true, action: null });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const say = useCallback((message: ReactNode, opts: { done?: boolean; action?: ToastState["action"]; ms?: number } = {}) => {
    setT({ message, on: true, done: opts.done ?? true, action: opts.action ?? null });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setT((x) => ({ ...x, on: false })), opts.ms ?? (opts.action ? 5000 : 2900));
  }, []);
  const hide = useCallback(() => setT((x) => ({ ...x, on: false })), []);
  const toast = (
    <div className={t.on ? "toast on" : "toast"} role="status" aria-live="polite">
      {t.done && <Icon name="check" />}
      <span>
        {t.message}
        {t.action && (
          <>
            {" "}
            <button
              type="button"
              className="t-undo"
              onClick={() => {
                t.action?.onClick();
                hide();
              }}
            >
              {t.action.label}
            </button>
          </>
        )}
      </span>
    </div>
  );
  return { toast, say };
}
